import { useState } from 'react';

interface FeedGamificationProps {
  onClose: () => void;
}

export function FeedGamification({ onClose }: FeedGamificationProps) {
  const [selectedTab, setSelectedTab] = useState<'stats' | 'badges' | 'ranking'>('stats');

  const socialStats = {
    totalXP: 2450,
    level: 8,
    nextLevelXP: 3000,
    postsShared: 45,
    commentsGiven: 128,
    likesReceived: 342,
    streak: 12
  };

  const socialBadges = [
    { id: '1', title: 'Influenciador', description: '100+ curtidas recebidas', icon: 'ri-star-line', color: 'from-amber-400 to-orange-500', unlocked: true },
    { id: '2', title: 'Comentarista Ativo', description: '50+ comentários dados', icon: 'ri-chat-3-line', color: 'from-blue-400 to-cyan-500', unlocked: true },
    { id: '3', title: 'Motivador', description: '30+ reações motivadoras', icon: 'ri-heart-line', color: 'from-red-400 to-pink-500', unlocked: true },
    { id: '4', title: 'Streak Master', description: '7 dias consecutivos', icon: 'ri-fire-line', color: 'from-orange-400 to-red-500', unlocked: true },
    { id: '5', title: 'Compartilhador', description: '20+ posts compartilhados', icon: 'ri-share-line', color: 'from-teal-400 to-cyan-500', unlocked: true },
    { id: '6', title: 'Mentor Social', description: '100+ comentários dados', icon: 'ri-user-star-line', color: 'from-purple-400 to-pink-500', unlocked: false },
    { id: '7', title: 'Viral', description: '500+ curtidas recebidas', icon: 'ri-rocket-line', color: 'from-indigo-400 to-purple-500', unlocked: false },
    { id: '8', title: 'Lenda Social', description: 'Nível 15 no feed', icon: 'ri-vip-crown-line', color: 'from-yellow-400 to-amber-500', unlocked: false }
  ];

  const topUsers = [
    { id: '1', name: 'Ana Silva', avatar: 'https://readdy.ai/api/search-image?query=professional%20businesswoman%20avatar%20portrait%20simple%20background%20corporate%20attire%20confident%20smile&width=100&height=100&seq=rank-ana-001&orientation=squarish', level: 12, xp: 4850, position: 1 },
    { id: '2', name: 'Carlos Mendes', avatar: 'https://readdy.ai/api/search-image?query=professional%20businessman%20avatar%20portrait%20simple%20background%20business%20suit%20friendly%20expression&width=100&height=100&seq=rank-carlos-002&orientation=squarish', level: 11, xp: 4320, position: 2 },
    { id: '3', name: 'Beatriz Costa', avatar: 'https://readdy.ai/api/search-image?query=professional%20businesswoman%20avatar%20portrait%20simple%20background%20elegant%20attire%20warm%20smile&width=100&height=100&seq=rank-beatriz-003&orientation=squarish', level: 10, xp: 3890, position: 3 },
    { id: '4', name: 'Você', avatar: 'https://readdy.ai/api/search-image?query=professional%20business%20person%20avatar%20portrait%20simple%20background%20corporate%20style%20friendly%20smile&width=100&height=100&seq=rank-you-004&orientation=squarish', level: 8, xp: 2450, position: 4 }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-amber-500 to-orange-500 p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
              <i className="ri-trophy-line text-2xl text-white"></i>
            </div>
            <div>
              <h2 className="text-2xl font-black text-white">XP Social</h2>
              <p className="text-white/80 text-sm">Suas estatísticas e conquistas</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <i className="ri-close-line text-xl text-white"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setSelectedTab('stats')}
            className={`flex-1 px-6 py-4 font-bold transition-colors ${
              selectedTab === 'stats'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <i className="ri-bar-chart-line mr-2"></i>
            Estatísticas
          </button>
          <button
            onClick={() => setSelectedTab('badges')}
            className={`flex-1 px-6 py-4 font-bold transition-colors ${
              selectedTab === 'badges'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <i className="ri-medal-line mr-2"></i>
            Distintivos
          </button>
          <button
            onClick={() => setSelectedTab('ranking')}
            className={`flex-1 px-6 py-4 font-bold transition-colors ${
              selectedTab === 'ranking'
                ? 'text-teal-600 border-b-2 border-teal-600'
                : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            <i className="ri-trophy-line mr-2"></i>
            Ranking
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Stats Tab */}
          {selectedTab === 'stats' && (
            <div className="space-y-6">
              {/* Level Progress */}
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Nível Social</p>
                    <p className="text-4xl font-black text-slate-800">{socialStats.level}</p>
                  </div>
                  <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center shadow-lg">
                    <i className="ri-vip-crown-line text-3xl text-white"></i>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Progresso</span>
                    <span className="font-bold text-slate-800">{socialStats.totalXP} / {socialStats.nextLevelXP} XP</span>
                  </div>
                  <div className="h-3 bg-white rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-teal-500 to-cyan-600 transition-all duration-500"
                      style={{ width: `${(socialStats.totalXP / socialStats.nextLevelXP) * 100}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center mb-3">
                    <i className="ri-share-line text-white text-xl"></i>
                  </div>
                  <p className="text-2xl font-black text-slate-800 mb-1">{socialStats.postsShared}</p>
                  <p className="text-sm text-slate-600">Posts Compartilhados</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mb-3">
                    <i className="ri-chat-3-line text-white text-xl"></i>
                  </div>
                  <p className="text-2xl font-black text-slate-800 mb-1">{socialStats.commentsGiven}</p>
                  <p className="text-sm text-slate-600">Comentários Dados</p>
                </div>

                <div className="bg-gradient-to-br from-red-50 to-pink-50 rounded-xl p-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-lg flex items-center justify-center mb-3">
                    <i className="ri-heart-line text-white text-xl"></i>
                  </div>
                  <p className="text-2xl font-black text-slate-800 mb-1">{socialStats.likesReceived}</p>
                  <p className="text-sm text-slate-600">Curtidas Recebidas</p>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-lg flex items-center justify-center mb-3">
                    <i className="ri-fire-line text-white text-xl"></i>
                  </div>
                  <p className="text-2xl font-black text-slate-800 mb-1">{socialStats.streak}</p>
                  <p className="text-sm text-slate-600">Dias de Streak</p>
                </div>
              </div>

              {/* XP Gains */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-bold text-slate-800 mb-3">Como Ganhar XP Social</h3>
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Compartilhar conquista</span>
                    <span className="font-bold text-teal-600">+50 XP</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Comentar em post</span>
                    <span className="font-bold text-teal-600">+10 XP</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Reagir a post</span>
                    <span className="font-bold text-teal-600">+5 XP</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Receber curtida</span>
                    <span className="font-bold text-teal-600">+2 XP</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">Streak diário</span>
                    <span className="font-bold text-teal-600">+20 XP</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Badges Tab */}
          {selectedTab === 'badges' && (
            <div className="grid grid-cols-2 gap-4">
              {socialBadges.map((badge) => (
                <div
                  key={badge.id}
                  className={`rounded-xl p-4 border-2 transition-all ${
                    badge.unlocked
                      ? 'bg-white border-slate-200 hover:border-teal-500'
                      : 'bg-slate-50 border-slate-200 opacity-50'
                  }`}
                >
                  <div className={`w-16 h-16 mx-auto mb-3 bg-gradient-to-br ${badge.color} rounded-xl flex items-center justify-center shadow-lg ${
                    !badge.unlocked && 'grayscale'
                  }`}>
                    <i className={`${badge.icon} text-3xl text-white`}></i>
                  </div>
                  <h4 className="font-bold text-slate-800 text-center mb-1">{badge.title}</h4>
                  <p className="text-xs text-slate-500 text-center">{badge.description}</p>
                  {badge.unlocked && (
                    <div className="mt-3 flex items-center justify-center gap-1 text-xs text-teal-600 font-bold">
                      <i className="ri-check-line"></i>
                      Desbloqueado
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Ranking Tab */}
          {selectedTab === 'ranking' && (
            <div className="space-y-3">
              {topUsers.map((user) => (
                <div
                  key={user.id}
                  className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                    user.name === 'Você'
                      ? 'bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-500'
                      : 'bg-white border border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex-shrink-0">
                    {user.position <= 3 ? (
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center font-black text-xl ${
                        user.position === 1 ? 'bg-gradient-to-br from-yellow-400 to-amber-500 text-white' :
                        user.position === 2 ? 'bg-gradient-to-br from-slate-300 to-slate-400 text-white' :
                        'bg-gradient-to-br from-orange-400 to-orange-500 text-white'
                      }`}>
                        {user.position}
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center font-black text-xl text-slate-600">
                        {user.position}
                      </div>
                    )}
                  </div>
                  <div className="relative flex-shrink-0">
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center text-xs font-bold text-white">
                      {user.level}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-slate-800">{user.name}</p>
                    <p className="text-sm text-slate-500">{user.xp.toLocaleString()} XP</p>
                  </div>
                  {user.position <= 3 && (
                    <i className={`ri-trophy-fill text-2xl ${
                      user.position === 1 ? 'text-yellow-500' :
                      user.position === 2 ? 'text-slate-400' :
                      'text-orange-500'
                    }`}></i>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
